/**
 * <flips-configurator>
 * Sorte -> (Mix: fix / selbst gemischt) -> Menge (Staffel) -> Einmalig / Abo -> Warenkorb.
 * Einzelne Sorte = Box-Variante (12 Packungen) x Anzahl Boxen. Mix = Einzelpackungen (70 g) je Sorte.
 * Staffelrabatte und Abo-Rabatt rechnet Shopify; die Preise hier sind nur die Vorschau.
 */
if (!customElements.get('flips-configurator')) {
  class FlipsConfigurator extends HTMLElement {
    connectedCallback() {
      this.root = this.querySelector('[data-flips-root]');
      try {
        this.cfg = JSON.parse(this.querySelector('[data-flips-config]').textContent);
      } catch (e) {
        return;
      }
      this.flavours = this.cfg.flavours;
      this.navigate = this.dataset.navigate === 'true';
      this.mixAvailable = this.flavours.length > 1 && this.flavours.every((f) => f.single && f.single.available) && (!this.navigate || !!this.cfg.mixUrl);
      const params = new URLSearchParams(window.location.search);
      const packsFromUrl = parseInt(params.get('menge') || this.dataset.presetPacks, 10);
      let flavourFromUrl = params.get('sorte') || this.dataset.presetFlavour;
      if (flavourFromUrl === this.cfg.mixHandle) flavourFromUrl = 'mix';
      this.state = {
        flavour: flavourFromUrl === 'mix' && this.mixAvailable ? 'mix' : this.flavours.some((f) => f.handle === flavourFromUrl) ? flavourFromUrl : this.flavours[0].handle,
        mixMode: (params.get('mix') || this.dataset.presetMix) === 'self' ? 'self' : 'fix',
        packs: this.cfg.tiers.some((t) => t.packs === packsFromUrl) ? packsFromUrl : this.cfg.tiers[0].packs,
        sub: false,
        plan: null,
        counts: {},
        busy: false,
        error: ''
      };
      this.resetCounts();
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => this.onChange(e));
      this.render();
    }

    onChange(e) {
      const sel = e.target.closest('[data-plan-select]');
      if (!sel || !this.contains(sel)) return;
      this.state.plan = parseInt(sel.value, 10);
      this.render();
    }

    // "Jeden Monat zustellen, 5 % Rabatt" -> "Jeden Monat"
    planLabel(p) {
      const short = String(p.name || '').split(',')[0].replace(/\s*zustellen\s*$/i, '').trim();
      return short || p.name;
    }

    get isMix() {
      return this.state.flavour === 'mix';
    }
    get current() {
      return this.flavours.find((f) => f.handle === this.state.flavour);
    }
    get tier() {
      return this.cfg.tiers.find((t) => t.packs === this.state.packs) || { pct: 0 };
    }

    resetCounts() {
      const n = this.flavours.length;
      const each = Math.floor(this.state.packs / n);
      this.flavours.forEach((f, i) => {
        this.state.counts[f.handle] = each + (i === 0 ? this.state.packs - each * n : 0);
      });
    }

    plans() {
      if (!this.isMix) return this.current.plans;
      const sets = this.flavours.map((f) => f.single.plans);
      if (sets.some((p) => !p.length)) return [];
      return sets[0].filter((p) => sets.every((s) => s.some((q) => q.id === p.id)));
    }

    money(cents) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'de', { style: 'currency', currency: this.dataset.currency }).format(cents / 100);
      } catch (e) {
        return (cents / 100).toFixed(2) + ' ' + this.dataset.currency;
      }
    }

    // Preislogik wie bei Shopify: Abo-Preis pro Stück gerundet, Staffelrabatt pro Stück abgerundet.
    unitAfter(unit, subPct, tierPct) {
      const u = subPct ? Math.round(unit * (1 - subPct / 100)) : unit;
      return u - Math.floor((u * tierPct) / 100);
    }

    lines(packs, counts) {
      if (!this.isMix) return [[this.current.price, packs / this.cfg.box]];
      const n = this.flavours.length;
      const each = Math.floor(packs / n);
      return this.flavours.map((f, i) => [f.single.price, counts ? counts[f.handle] || 0 : each + (i === 0 ? packs - each * n : 0)]);
    }

    priceFor(packs, subPct, counts) {
      const tier = this.cfg.tiers.find((t) => t.packs === packs) || { pct: 0 };
      return this.lines(packs, counts).reduce((sum, [unit, qty]) => sum + this.unitAfter(unit, subPct, tier.pct) * qty, 0);
    }

    selectedSubPct() {
      const plan = this.state.sub ? this.plans().find((p) => p.id === this.state.plan) : null;
      return plan ? plan.pct : 0;
    }

    mixTotal() {
      return this.flavours.reduce((s, f) => s + (this.state.counts[f.handle] || 0), 0);
    }

    mixValid() {
      return !this.isMix || this.state.mixMode === 'fix' || this.mixTotal() === this.state.packs;
    }

    totals() {
      const counts = this.isMix && this.state.mixMode === 'self' ? this.state.counts : null;
      const base = this.lines(this.state.packs, counts).reduce((sum, [unit, qty]) => sum + unit * qty, 0);
      const total = this.priceFor(this.state.packs, this.selectedSubPct(), counts);
      return { base, total };
    }

    onClick(e) {
      const el = e.target.closest('[data-act]');
      if (!el || !this.contains(el)) return;
      const act = el.dataset.act;
      const s = this.state;
      s.error = '';
      if (act === 'flavour') {
        if (this.navigate && el.dataset.value !== s.flavour) {
          const target = el.dataset.value === 'mix' ? this.cfg.mixUrl : this.flavours.find((f) => f.handle === el.dataset.value).url;
          const q = new URLSearchParams({ menge: s.packs });
          if (el.dataset.value === 'mix') q.set('mix', s.mixMode);
          window.location.href = target + '?' + q.toString();
          return;
        }
        s.flavour = el.dataset.value;
        s.sub = false;
        s.plan = null;
      } else if (act === 'mixmode') {
        s.mixMode = el.dataset.value;
        this.resetCounts();
      } else if (act === 'packs') {
        s.packs = parseInt(el.dataset.value, 10);
        this.resetCounts();
      } else if (act === 'sub') {
        s.sub = el.dataset.value === '1';
        const plans = this.plans();
        s.plan = s.sub && plans.length ? (plans.find((p) => p.id === s.plan) || plans[0]).id : null;
      } else if (act === 'plan') {
        s.plan = parseInt(el.dataset.value, 10);
      } else if (act === 'inc' || act === 'dec') {
        const h = el.dataset.value;
        const delta = act === 'inc' ? 1 : -1;
        const next = (s.counts[h] || 0) + delta;
        if (next < 0) return;
        if (delta > 0 && this.mixTotal() >= s.packs) return;
        s.counts[h] = next;
      } else if (act === 'add') {
        this.addToCart();
        return;
      }
      this.render();
    }

    async addToCart() {
      const s = this.state;
      if (s.busy || !this.mixValid()) return;
      const plan = s.sub ? s.plan : null;
      let items;
      if (this.isMix) {
        items = this.flavours
          .filter((f) => (s.counts[f.handle] || 0) > 0)
          .map((f) => ({ id: f.single.variant, quantity: s.counts[f.handle], properties: { _konfigurator: 'mix-' + s.packs } }));
      } else {
        items = [{ id: this.current.variant, quantity: s.packs / this.cfg.box }];
      }
      if (plan) items.forEach((i) => (i.selling_plan = plan));
      s.busy = true;
      this.render();
      try {
        const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.description || json.message || 'Fehler');
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
      } catch (err) {
        s.error = err.message || 'Das hat leider nicht geklappt.';
      } finally {
        s.busy = false;
        this.render();
      }
    }

    tile(act, value, selected, inner, extra = '') {
      return `<button type="button" class="flips-tile${selected ? ' is-selected' : ''} ${extra}" data-act="${act}" data-value="${value}" aria-pressed="${selected}">${inner}</button>`;
    }

    render() {
      const s = this.state;
      const t = this.totals();
      const plans = this.plans();
      let html = '';

      // 1 Sorte
      html += `<div class="flips-step"><p class="flips-step__title"><span>1</span> Sorte</p><div class="flips-grid flips-grid--flavour">`;
      this.flavours.forEach((f) => {
        const img = f.image ? `<img src="${f.image}" alt="" loading="lazy" width="120" height="120">` : '';
        html += this.tile('flavour', f.handle, s.flavour === f.handle, `${img}<span class="flips-tile__label">${f.label}</span>`, 'flips-tile--flavour');
      });
      if (this.mixAvailable) {
        const imgs = this.flavours.slice(0, 3).map((f) => (f.image ? `<img src="${f.image}" alt="" loading="lazy" width="60" height="60">` : '')).join('');
        html += this.tile('flavour', 'mix', this.isMix, `<span class="flips-tile__stack">${imgs}</span><span class="flips-tile__label">Mix</span>`, 'flips-tile--flavour');
      }
      html += `</div></div>`;

      // 2 Mix: fix / selbst
      let n = 2;
      if (this.isMix) {
        html += `<div class="flips-step"><p class="flips-step__title"><span>${n++}</span> Zusammenstellung</p><div class="flips-grid flips-grid--2">`;
        html += this.tile('mixmode', 'fix', s.mixMode === 'fix', `<span class="flips-tile__label">Fix gemischt</span><span class="flips-tile__sub">Von jeder Sorte gleich viel</span>`);
        html += this.tile('mixmode', 'self', s.mixMode === 'self', `<span class="flips-tile__label">Selbst gemischt</span><span class="flips-tile__sub">Du wählst die Mengen</span>`);
        html += `</div>`;
        if (s.mixMode === 'fix') {
          const each = s.packs / this.flavours.length;
          html += `<p class="flips-hint">${Number.isInteger(each) ? each + ' × ' : ''}${this.flavours.map((f) => f.label).join(', ')}</p>`;
        } else {
          const rest = s.packs - this.mixTotal();
          html += `<div class="flips-mix">`;
          this.flavours.forEach((f) => {
            html += `<div class="flips-mix__row"><span class="flips-mix__name">${f.label}</span><span class="flips-stepper"><button type="button" data-act="dec" data-value="${f.handle}" aria-label="Weniger ${f.label}">−</button><output>${s.counts[f.handle] || 0}</output><button type="button" data-act="inc" data-value="${f.handle}" aria-label="Mehr ${f.label}">+</button></span></div>`;
          });
          html += `<p class="flips-hint${rest === 0 ? ' is-ok' : ''}">${rest === 0 ? `Perfekt – ${s.packs} Packungen zusammengestellt.` : `Noch ${rest} Packung${rest === 1 ? '' : 'en'} auswählen.`}</p></div>`;
        }
        html += `</div>`;
      }

      // 3 Menge
      html += `<div class="flips-step"><p class="flips-step__title"><span>${n++}</span> Menge</p><div class="flips-grid flips-grid--packs">`;
      // Labels: erste Menge mit Gratisversand (ab Versandgrenze des Marktes), letzte Menge = Bestpreis
      const freeFrom = this.cfg.freeShipFrom || 0;
      const freeIdx = freeFrom ? this.cfg.tiers.findIndex((t) => this.priceFor(t.packs, 0, null) >= freeFrom) : -1;
      const bestIdx = this.cfg.tiers.length - 1;
      this.cfg.tiers.forEach((tier, i) => {
        const per = this.priceFor(tier.packs, this.selectedSubPct(), null) / tier.packs;
        const badge = tier.pct ? `<span class="flips-tile__badge">−${tier.pct} %</span>` : '';
        const tag = i === bestIdx ? 'Bestpreis' : i === freeIdx ? 'Gratis Versand' : '';
        const tagHtml = tag ? `<span class="flips-tile__tag">${tag}</span>` : '';
        const boxes = tier.packs / this.cfg.box;
        html += this.tile('packs', tier.packs, s.packs === tier.packs, `${tagHtml}${badge}<span class="flips-tile__big">${tier.packs}</span><span class="flips-tile__label">Packungen</span><span class="flips-tile__sub">${boxes} ${boxes === 1 ? 'Box' : 'Boxen'}</span><span class="flips-tile__price">${this.money(per)} / Stk.</span>`, 'flips-tile--pack');
      });
      html += `</div></div>`;

      // 4 Lieferung
      html += `<div class="flips-step"><p class="flips-step__title"><span>${n++}</span> Lieferung</p><div class="flips-opts">`;
      html += `<div class="flips-opt${!s.sub ? ' is-selected' : ''}"><button type="button" class="flips-opt__head" data-act="sub" data-value="0" aria-pressed="${!s.sub}"><span class="flips-opt__radio"></span><span class="flips-opt__main"><span class="flips-opt__title">Einmalige Lieferung</span></span><span class="flips-opt__price">${this.money(this.priceFor(s.packs, 0, null) / s.packs)} / Stk.</span></button></div>`;
      if (plans.length) {
        const best = Math.max(...plans.map((p) => p.pct));
        const subPer = this.priceFor(s.packs, s.sub ? this.selectedSubPct() : best, null) / s.packs;
        html += `<div class="flips-opt${s.sub ? ' is-selected' : ''}"><button type="button" class="flips-opt__head" data-act="sub" data-value="1" aria-pressed="${s.sub}"><span class="flips-opt__radio"></span><span class="flips-opt__main"><span class="flips-opt__title">Im Abo</span>${best ? `<span class="flips-opt__badge">Spare ${best} %</span>` : ''}</span><span class="flips-opt__price">${this.money(subPer)} / Stk.</span></button>`;
        html += `<p class="flips-opt__note">Jederzeit kündbar, pausierbar oder änderbar.</p>`;
        if (s.sub) {
          html += `<div class="flips-opt__select"><label for="flips-plan-select">Lieferintervall</label><select id="flips-plan-select" class="flips-select" data-plan-select>`;
          plans.forEach((p) => {
            html += `<option value="${p.id}"${s.plan === p.id ? ' selected' : ''}>${this.planLabel(p)}</option>`;
          });
          html += `</select></div>`;
        }
        html += `</div>`;
      }
      html += `</div></div>`;

      // Summary
      const valid = this.mixValid();
      const sale = t.total < t.base - 0.5;
      html += `<div class="flips-summary"><div class="flips-summary__price">${sale ? `<s>${this.money(t.base)}</s> ` : ''}<strong>${this.money(t.total)}</strong><span>inkl. MwSt.</span><span>· ${this.money(t.total / (s.packs * (this.cfg.packGrams || 70)) * 100)} / 100 g</span></div>`;
      html += `<button type="button" class="btn btn--primary flips-add${s.busy ? ' btn--loading' : ''}" data-act="add" ${!valid || s.busy ? 'disabled' : ''}>In den Warenkorb</button>`;
      if (s.error) html += `<p class="flips-error" role="alert">${s.error}</p>`;
      html += `</div>`;

      this.root.innerHTML = html;
    }
  }
  customElements.define('flips-configurator', FlipsConfigurator);
}
